import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Loader2, Play, RefreshCw } from "lucide-react";
import {
  CHECKPOINT_LABELS,
  DATA_TRIGGERS,
  type DataTriggerDef,
  type DataTriggerKey,
} from "../../../../../shared/index";
import { api, ApiError, isLikelyTimeoutError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";

const CHECKPOINTS_QUERY = ["data", "checkpoints"] as const;

const STARTED_COPY =
  "Trigger started — it may run for several minutes. Verify progress on the checkpoint board; do not re-run.";

// Widen the literal union so optional fields are uniformly accessible.
const ALL_TRIGGERS: readonly DataTriggerDef[] = DATA_TRIGGERS;
const triggerByCheckpoint = new Map<string, DataTriggerDef>(
  ALL_TRIGGERS.filter((t) => t.checkpointKey !== undefined).map((t) => [
    t.checkpointKey!,
    t,
  ])
);
const etlTriggers = ALL_TRIGGERS.filter((t) => t.app === "stockEtl");

export default function DataPipelinePage() {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState<DataTriggerDef | null>(null);
  // Versions from the previous fetch, to badge rows whose version advanced.
  const prevVersionsRef = useRef<Map<string, number> | null>(null);
  const [deltas, setDeltas] = useState<Map<string, number>>(new Map());

  const checkpointsQuery = useQuery({
    queryKey: CHECKPOINTS_QUERY,
    queryFn: api.data.getCheckpoints,
  });
  const data = checkpointsQuery.data;

  useEffect(() => {
    if (!data) return;
    const prev = prevVersionsRef.current;
    if (prev) {
      const next = new Map<string, number>();
      for (const cp of data.checkpoints) {
        const before = prev.get(cp.key);
        if (before !== undefined && cp.changeTrackingVersion > before) {
          next.set(cp.key, cp.changeTrackingVersion - before);
        }
      }
      setDeltas(next);
    }
    prevVersionsRef.current = new Map(
      data.checkpoints.map((cp) => [cp.key, cp.changeTrackingVersion])
    );
  }, [data]);

  const runMutation = useMutation({
    mutationFn: (key: string) => api.data.runTrigger(key as DataTriggerKey),
    onSuccess: (result, key) => {
      const def = DATA_TRIGGERS.find((t) => t.key === key);
      const label = def?.label ?? key;
      if (result.status === "completed") {
        toast.success(`${label}: ${result.message ?? "completed"}`);
        queryClient.invalidateQueries({ queryKey: CHECKPOINTS_QUERY });
      } else if (result.status === "started") {
        toast.info(`${label}: ${STARTED_COPY}`);
      } else {
        toast.error(`${label} failed: ${result.message ?? "unknown error"}`);
      }
    },
    onError: (e, key) => {
      const def = DATA_TRIGGERS.find((t) => t.key === key);
      const label = def?.label ?? key;
      if (isLikelyTimeoutError(e)) {
        toast.info(
          `${label}: the request timed out but the trigger may still be running. Verify on the checkpoint board before re-running.`
        );
      } else {
        toast.error(
          `${label} failed: ${e instanceof ApiError ? e.message : "Something went wrong"}`
        );
      }
    },
  });

  const runningKey = runMutation.isPending ? runMutation.variables : null;

  const requestRun = (def: DataTriggerDef) => {
    // Bulk ETL recomputes are expensive — confirm before firing.
    if (def.app === "stockEtl") {
      setConfirming(def);
    } else {
      runMutation.mutate(def.key);
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <Link
          to="/dashboard/data"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Data
        </Link>
        <h1 className="text-2xl font-bold">Pipeline & Triggers</h1>
        <p className="text-sm text-muted-foreground">
          Change-tracking checkpoints of the C# data pipeline. Running a scan
          enqueues whatever changed since the last checkpoint; the version
          advances when the scan commits.
        </p>
      </div>

      {/* ── Checkpoint board ── */}
      <div className="rounded-lg border bg-card">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b px-6 py-4">
          <div className="flex items-center gap-3">
            <h2 className="font-semibold">Domain checkpoints</h2>
            {data && (
              <Badge variant={data.env === "prod" ? "destructive" : "secondary"}>
                {data.env}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-3">
            {data && (
              <span className="text-xs text-muted-foreground">
                Fetched at {new Date(data.fetchedAt).toLocaleTimeString()}
              </span>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => checkpointsQuery.refetch()}
              disabled={checkpointsQuery.isFetching}
            >
              {checkpointsQuery.isFetching ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Refresh
            </Button>
          </div>
        </div>

        {data && data.duplicateKeys.length > 0 && (
          <div className="border-b bg-warning/5 px-6 py-3 text-sm">
            <p className="font-medium">
              {data.duplicateKeys.length} domain(s) have duplicate entries in
              the checkpoint blob
            </p>
            <p className="mt-1 text-muted-foreground">
              A TriggerManager write bug appends a new key twice, then only ever
              updates the first copy. The live (first) value is shown below; the
              stale copies are ignored — same as the pipeline's own reader does.
              Affected:{" "}
              <span className="font-mono text-xs">
                {data.duplicateKeys.join(", ")}
              </span>
            </p>
          </div>
        )}

        {checkpointsQuery.isLoading ? (
          <div className="flex items-center justify-center gap-2 px-6 py-12 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading checkpoints…
          </div>
        ) : checkpointsQuery.isError ? (
          <div className="px-6 py-6">
            <div className="rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {checkpointsQuery.error instanceof ApiError
                ? checkpointsQuery.error.message
                : "Failed to load checkpoints"}
            </div>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Domain</TableHead>
                <TableHead>Checkpoint key</TableHead>
                <TableHead className="text-right">Version</TableHead>
                <TableHead className="w-32" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.checkpoints.map((cp) => {
                const trigger = triggerByCheckpoint.get(cp.key);
                const delta = deltas.get(cp.key);
                const isRunning = runningKey === trigger?.key;
                return (
                  <TableRow key={cp.key}>
                    <TableCell className="font-medium">
                      {CHECKPOINT_LABELS[cp.key] ?? cp.key}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {cp.key}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {cp.changeTrackingVersion.toLocaleString()}
                      {delta !== undefined && (
                        <Badge variant="success" className="ml-2">
                          +{delta.toLocaleString()}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {trigger ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => requestRun(trigger)}
                          disabled={runMutation.isPending}
                        >
                          {isRunning ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Play className="h-4 w-4" />
                          )}
                          Run scan
                        </Button>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          timer only
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {/* ── Bulk ETL triggers ── */}
      <div className="rounded-lg border bg-card">
        <div className="border-b px-6 py-4">
          <h2 className="font-semibold">Bulk ETL runs</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Full recomputes on the Stock-ETL app. These can run for many
            minutes; the admin panel reports them as started and lets them
            finish in the background.
          </p>
        </div>
        <div className="grid gap-4 p-6 sm:grid-cols-2 lg:grid-cols-3">
          {etlTriggers.map((def) => {
            const isRunning = runningKey === def.key;
            return (
              <div
                key={def.key}
                className="flex flex-col justify-between rounded-lg border bg-muted/20 p-4"
              >
                <div>
                  <p className="font-medium">{def.label}</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {def.description}
                  </p>
                  {def.duration === "slow" && (
                    <Badge variant="warning" className="mt-2">
                      long-running
                    </Badge>
                  )}
                </div>
                <div className="mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => requestRun(def)}
                    disabled={runMutation.isPending}
                  >
                    {isRunning ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Play className="h-4 w-4" />
                    )}
                    Run
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Confirm dialog for bulk ETL triggers */}
      <Dialog
        open={confirming !== null}
        onOpenChange={(open) => !open && setConfirming(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Run {confirming?.label}?</DialogTitle>
            <DialogDescription>
              {confirming?.description}. This recomputes across the whole
              universe and may run for many minutes on the data platform.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirming(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (confirming) runMutation.mutate(confirming.key);
                setConfirming(null);
              }}
            >
              Run it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
